<script setup>
import { useApi } from '@/utils/api'
import { useRouter } from 'vue-router'

// Module 15 (§9.2) — "reuse Master Data Employee dengan tambahan kolom
// status STR/SIP + ReminderBadge.vue" (blueprint §Module15 Vue Page).
// Halaman TERPISAH dari pages/master-data/employees/index.vue (bukan
// menambah kolom di tempat) — CRUD dasar Employee tetap murni Master Data,
// halaman ini murni pandangan HR (baca saja, tidak ada create/edit/delete),
// reuse GET /api/v1/master-data/employees yang sama, tidak ada endpoint
// list baru.
definePage({
  meta: {
    layout: 'default',
    action: 'credential-manage',
    subject: 'Hr',
  },
})

const router = useRouter()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const fetchItems = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/master-data/employees', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = response?.meta?.total ?? data?.meta?.total ?? 0
  } catch (e) {
    console.error('Fetch employees error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchItems)
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const headers = [
  { title: 'No. Pegawai', key: 'employee_number', width: 130 },
  { title: 'Nama', key: 'name' },
  { title: 'Unit', key: 'unit' },
  { title: 'STR', key: 'str', align: 'center' },
  { title: 'SIP', key: 'sip', align: 'center' },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 100 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-bold">
          Kredensial Pegawai
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Status STR/SIP & penilaian kinerja pegawai (§9.2)
        </p>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari nama atau nomor pegawai..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
        @click:row="(e, { item }) => router.push({ name: 'hr-employees-id-credential', params: { id: item.id } })"
      >
        <template #item.unit="{ item }">
          {{ item.unit?.name ?? '-' }}
        </template>
        <template #item.str="{ item }">
          <CredentialStatusChip
            label="STR"
            :expiry-date="item.str_expiry_date"
          />
        </template>
        <template #item.sip="{ item }">
          <CredentialStatusChip
            label="SIP"
            :expiry-date="item.sip_expiry_date"
          />
        </template>
        <template #item.actions="{ item }">
          <VBtn
            icon="ri-file-shield-2-line"
            variant="text"
            size="small"
            @click.stop="router.push({ name: 'hr-employees-id-credential', params: { id: item.id } })"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data pegawai
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>
  </div>
</template>
